import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import {
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from "reactstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Auth, Storage } from "aws-amplify";
import Editable from "react-x-editable";

import {
    getJournalDays,
    getJournalByDay,
    createJournalEntry,
    updateJournalEntry,
    deleteJournalEntry,
    getJournalTimeline,
} from "../../store/journal/actions";
import { getAreas } from "../../store/goals/actions";

// ---------- helpers ----------
function toDateKey(d) {
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function formatDateShort(dateKey) {
    if (!dateKey) return "";
    const [y, m, d] = dateKey.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateHuman(dateKey) {
    if (!dateKey) return "";
    const [y, m, d] = dateKey.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
}

function safeArray(x) {
    return Array.isArray(x) ? x : [];
}

function safeString(x) {
    return typeof x === "string" ? x : "";
}

function lookupAreaName(userareas, areaId) {
    if (!areaId) return "";
    const area = safeArray(userareas).find((a) => a.id === areaId);
    return area?.name || areaId;
}

function lookupPlantName(userareas, areaId, plantId) {
    if (!plantId) return "";
    const area = safeArray(userareas).find((a) => a.id === areaId);
    const plant = safeArray(area?.plants).find((p) => p.id === plantId);
    return plant ? (plant.species || plant.name || plantId) : plantId;
}

function pickNoteId(note) {
    return note?.note_id || note?.id || note?.noteId || null;
}

function normalizePhotos(note) {
    const photos = safeArray(note?.photos);
    return photos
        .map((p) => {
            const s3_key = p?.s3_key || p?.s3Key || p?.key || "";
            if (!s3_key) return null;
            return { s3_key, comment: safeString(p?.comment) };
        })
        .filter(Boolean);
}

async function safeStorageGet(rawKey) {
    if (!rawKey) return null;
    // Amplify v5's Storage.get() prepends the access-level prefix itself, but keys written by
    // Observer (Amplify v6, which requires the prefix in the path) already include it.
    const key = typeof rawKey === "string" ? rawKey.replace(/^(public|protected|private)\//, "") : rawKey;
    try {
        return await Storage.get(key);
    } catch (e1) {
        try { return await Storage.get(key, { level: "public" }); } catch (e2) {
            try { return await Storage.get(key, { level: "protected" }); } catch (e3) {
                console.error("Storage.get failed for key:", key, e1);
                return null;
            }
        }
    }
}

async function uploadPhotoToS3({ file, username, dateKey, noteId }) {
    const cleanName = file?.name || "photo";
    const prefix = noteId
        ? `${username}/journal/${dateKey}/${noteId}`
        : `${username}/journal/${dateKey}`;
    const key = `${prefix}/${Date.now()}_${cleanName}`;
    await Storage.put(key, file, { contentType: file.type });
    return key;
}

// ---------- dummy timeline data (replace with real API data later) ----------
const DUMMY_TIMELINE_NOTES = [
    { date: "2026-03-01", area_id: "dummy-area-1", plant_id: "dummy-plant-1", note_text: "Pruned the lower branches. Saw good bud development on the upper scaffold.", photos: [] },
    { date: "2026-03-08", area_id: "dummy-area-1", plant_id: "",              note_text: "Added 3 inches of wood chip mulch around the drip line. Soil looks moist underneath.", photos: [] },
    { date: "2026-03-15", area_id: "dummy-area-1", plant_id: "dummy-plant-1", note_text: "Leaves starting to break from buds. About 10 days ahead of last year.", photos: [] },
    { date: "2026-03-18", area_id: "dummy-area-1", plant_id: "",              note_text: "Inspected for aphids — none found. Added sticky trap near the base as a precaution.", photos: [] },
    { date: "2026-03-22", area_id: "dummy-area-1", plant_id: "dummy-plant-1", note_text: "First bloom spotted on the south-facing branch. Very exciting!", photos: [] },
];

// ---------- TimelineView component ----------
const TimelineView = ({
    userareas,
    areaOptions,
    timelineArea,
    setTimelineArea,
    timelinePlant,
    setTimelinePlant,
    timelinePlantOptions,
    timelineNotes,
    timelineLoading,
    photoUrlByKey,
    onPhotoClick,
}) => {
    const [selectedIdx, setSelectedIdx] = useState(null);

    // Reset selection when area/plant changes
    useEffect(() => { setSelectedIdx(null); }, [timelineArea, timelinePlant]);

    const notes = safeArray(timelineNotes);

    const selectedNote = selectedIdx !== null ? notes[selectedIdx] : null;

    return (
        <div>
            {/* Selectors */}
            <Row className="mb-3" style={{ alignItems: "flex-end" }}>
                <Col xs={12} sm={5} md={4}>
                    <Label style={{ fontSize: 12, marginBottom: 2 }}>Area</Label>
                    <Input type="select" bsSize="sm" value={timelineArea}
                        onChange={(e) => setTimelineArea(e.target.value)}>
                        <option value="">— All areas —</option>
                        {areaOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </Input>
                </Col>
                <Col xs={12} sm={5} md={4}>
                    <Label style={{ fontSize: 12, marginBottom: 2 }}>
                        Plant <span style={{ color: "rgba(0,0,0,0.4)", fontWeight: 400 }}>(optional)</span>
                    </Label>
                    <Input type="select" bsSize="sm" value={timelinePlant}
                        onChange={(e) => setTimelinePlant(e.target.value)}
                        disabled={!timelineArea || timelinePlantOptions.length === 0}>
                        <option value="">— All plants —</option>
                        {timelinePlantOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Input>
                </Col>
            </Row>

            {!timelineArea ? (
                <div style={{ color: "rgba(0,0,0,0.45)", padding: "24px 0" }}>Select an area to view its timeline.</div>
            ) : timelineLoading ? (
                <div style={{ color: "rgba(0,0,0,0.45)", padding: "24px 0" }}>Loading…</div>
            ) : notes.length === 0 ? (
                <div style={{ color: "rgba(0,0,0,0.45)", padding: "24px 0" }}>No notes found for this selection.</div>
            ) : (
                <>
                    {/* Rail */}
                    <div style={{ overflowX: "auto", paddingBottom: 8 }}>
                        <div style={{ position: "relative", minWidth: notes.length * 100 + 40 + "px", padding: "0 20px" }}>
                            {/* Horizontal line — pinned to dot centre (dot is 18px, sits at top 0 of its row, so line at 9px) */}
                            <div style={{
                                position: "absolute",
                                top: 9, left: 20, right: 20,
                                height: 2,
                                background: "#BEC8B6",
                                zIndex: 0,
                            }} />

                            <div style={{ display: "flex", alignItems: "flex-start" }}>
                                {notes.map((note, idx) => {
                                    const isSelected = selectedIdx === idx;
                                    const label = note.date
                                        ? new Date(...note.date.split("-").map((v, i) => i === 1 ? Number(v) - 1 : Number(v)))
                                            .toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                        : "";

                                    return (
                                        <div key={idx} style={{ flex: "0 0 100px", display: "flex", flexDirection: "column", alignItems: "center", position: "relative", zIndex: 1, cursor: "pointer" }}
                                            onClick={() => setSelectedIdx(isSelected ? null : idx)}>
                                            {/* Dot — always at top of column so it aligns with the fixed rail */}
                                            <div style={{
                                                width: 18, height: 18,
                                                borderRadius: "50%",
                                                background: isSelected ? "#4a6741" : "#6c757d",
                                                border: "2px solid #fff",
                                                boxShadow: isSelected ? "0 0 0 3px #4a6741" : "0 0 0 2px #BEC8B6",
                                                transition: "background 0.15s, box-shadow 0.15s",
                                                flexShrink: 0,
                                            }} />
                                            {/* Date label */}
                                            <div style={{
                                                marginTop: 8,
                                                fontSize: 11,
                                                color: isSelected ? "#4a6741" : "rgba(0,0,0,0.5)",
                                                fontWeight: isSelected ? 600 : 400,
                                                textAlign: "center",
                                                whiteSpace: "nowrap",
                                            }}>
                                                {label}
                                            </div>
                                            {/* Plant indicator — always rendered to keep column height uniform */}
                                            {(() => {
                                                const plantLabel = lookupPlantName(userareas, note.area_id, note.plant_id);
                                                return (
                                                    <div style={{
                                                        fontSize: 10,
                                                        color: "rgba(0,0,0,0.35)",
                                                        marginTop: 2,
                                                        textAlign: "center",
                                                        whiteSpace: "nowrap",
                                                        maxWidth: 90,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        visibility: plantLabel ? "visible" : "hidden",
                                                    }}>
                                                        {plantLabel || "x"}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Detail card for selected note */}
                    {selectedNote && (
                        <div style={{
                            marginTop: 12,
                            border: "1px solid rgba(0,0,0,0.1)",
                            borderRadius: 10,
                            padding: "14px 16px",
                            background: "#fff",
                            boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
                        }}>
                            <div style={{ fontWeight: 600, color: "#4a6741", marginBottom: 6 }}>
                                {formatDateHuman(selectedNote.date)}
                            </div>
                            <div style={{ marginBottom: 8 }}>
                                {selectedNote.area_id && <span className="note-tag">📍 {lookupAreaName(userareas, selectedNote.area_id)}</span>}
                                {selectedNote.plant_id && <span className="note-tag">🌿 {lookupPlantName(userareas, selectedNote.area_id, selectedNote.plant_id)}</span>}
                            </div>
                            {selectedNote.note_text && (
                                <div style={{ color: "rgba(0,0,0,0.75)", lineHeight: 1.5, marginBottom: 8 }}>
                                    {selectedNote.note_text}
                                </div>
                            )}
                            {normalizePhotos(selectedNote).length > 0 && (
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                                    {normalizePhotos(selectedNote).map((p) => {
                                        const url = photoUrlByKey[p.s3_key];
                                        return url ? (
                                            <img key={p.s3_key} src={url} alt={p.comment || "photo"}
                                                style={{ width: 70, height: 70, objectFit: "cover", borderRadius: 8, cursor: "pointer", border: "1px solid rgba(0,0,0,0.08)" }}
                                                onClick={() => onPhotoClick(url)}
                                            />
                                        ) : null;
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ---------- component ----------
const JournalLog = (props) => {
    const {
        daysWithNotes,
        notes,
        timelineNotes,
        timelineLoading,
        error,
        userareas,

        onGetJournalDays,
        onGetJournalByDay,
        onCreateNote,
        onUpdateNote,
        onGetAreas,
        onDeleteNote,
        onGetJournalTimeline,
    } = props;

    const calendarRef = useRef(null);

    const [username, setUsername] = useState("");
    const [monthLabel, setMonthLabel] = useState("");
    const [selectedDateKey, setSelectedDateKey] = useState(() => toDateKey(new Date()));

    // Note area/plant editing
    const [editingNoteId, setEditingNoteId] = useState(null);

    // Add Note modal state
    const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);
    const [newNoteText, setNewNoteText] = useState("");
    const [newNoteArea, setNewNoteArea] = useState("");
    const [newNotePlant, setNewNotePlant] = useState("");
    const [newNoteFiles, setNewNoteFiles] = useState([]);
    const [creating, setCreating] = useState(false);
    const [pendingPhotos, setPendingPhotos] = useState(null); // { files, dateKey, noteText }

    // Add Photo modal (for existing notes)
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [activeNoteId, setActiveNoteId] = useState(null);
    const [photoCaption, setPhotoCaption] = useState("");
    const [uploading, setUploading] = useState(false);

    // Lightbox
    const [lightboxUrl, setLightboxUrl] = useState(null);

    // Resolved photo URLs, keyed by s3_key
    const [photoUrlByKey, setPhotoUrlByKey] = useState({});

    // Photo carousel index
    const [carouselIdx, setCarouselIdx] = useState(0);

    // View: "notes" | "timeline"
    const [journalView, setJournalView] = useState("notes");
    const [searchTerm, setSearchTerm] = useState("");

    // Timeline selectors
    const [timelineArea, setTimelineArea] = useState("");
    const [timelinePlant, setTimelinePlant] = useState("");

    // ---------- auth bootstrap ----------
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const user = await Auth.currentAuthenticatedUser();
                if (!cancelled) setUsername(user?.username || "");
            } catch (e) {
                console.error("Auth.currentAuthenticatedUser failed:", e);
                if (!cancelled) setUsername("");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ---------- fetch days list + areas ----------
    useEffect(() => {
        if (!username) return;
        try { onGetJournalDays(username); } catch (e) { console.error("onGetJournalDays failed:", e); }
        try { onGetAreas(); } catch (e) { console.error("onGetAreas failed:", e); }
        try { onGetJournalTimeline({ username }); } catch (e) { console.error("onGetJournalTimeline failed:", e); }
    }, [username, onGetJournalDays, onGetAreas]);

    // ---------- fetch notes for selected day ----------
    useEffect(() => {
        if (!username || !selectedDateKey) return;
        try { onGetJournalByDay(username, selectedDateKey); } catch (e) { console.error("onGetJournalByDay failed:", e); }
    }, [username, selectedDateKey, onGetJournalByDay]);

    // ---------- calendar events ----------
    const miniCalendarEvents = useMemo(() => {
        return safeArray(daysWithNotes)
            .map((d) => safeString(d?.date || d?.day || d))
            .filter(Boolean)
            .map((dk) => ({ id: `marker-${dk}`, start: dk, allDay: true, title: "" }));
    }, [daysWithNotes]);

    // ---------- all photos for the day (carousel) ----------
    const allPhotosForDay = useMemo(() => {
        const result = [];
        for (const note of safeArray(notes)) {
            for (const p of normalizePhotos(note)) {
                result.push({ ...p, noteText: safeString(note?.note_text || note?.text) });
            }
        }
        return result;
    }, [notes]);

    // Reset carousel when day changes
    useEffect(() => { setCarouselIdx(0); }, [selectedDateKey]);

    // ---------- resolve photo URLs ----------
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const allKeys = new Set();
                for (const n of [...safeArray(notes), ...safeArray(timelineNotes)]) {
                    for (const p of normalizePhotos(n)) allKeys.add(p.s3_key);
                }
                const toFetch = Array.from(allKeys).filter((k) => k && !photoUrlByKey[k]);
                if (!toFetch.length) return;
                const updates = {};
                for (const k of toFetch) {
                    const url = await safeStorageGet(k);
                    if (url) updates[k] = url;
                }
                if (!cancelled && Object.keys(updates).length) {
                    setPhotoUrlByKey((prev) => ({ ...prev, ...updates }));
                }
            } catch (e) {
                console.error("Photo URL hydration failed:", e);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notes, timelineNotes]);

    // ---------- upload pending photos once new note appears ----------
    useEffect(() => {
        if (!pendingPhotos || !pendingPhotos.files.length || !notes.length || !username) return;

        // Find the note we just created: match by text and date, no existing photos
        const newNote = notes.find((n) => {
            const matchesDate = safeString(n.date) === pendingPhotos.dateKey;
            const matchesText = safeString(n.note_text || n.text) === pendingPhotos.noteText;
            const hasNoPhotos = normalizePhotos(n).length === 0;
            return matchesDate && matchesText && hasNoPhotos;
        });
        if (!newNote) return;

        const noteId = pickNoteId(newNote);
        if (!noteId) return;

        const { files, dateKey } = pendingPhotos;
        setPendingPhotos(null); // clear before async to avoid re-running

        (async () => {
            try {
                const uploadedPhotos = [];
                for (const file of files) {
                    const s3_key = await uploadPhotoToS3({ file, username, dateKey, noteId });
                    const photoUrl = await safeStorageGet(s3_key);
                    if (photoUrl) setPhotoUrlByKey((prev) => ({ ...prev, [s3_key]: photoUrl }));
                    uploadedPhotos.push({ s3_key, comment: "" });
                }
                await onUpdateNote({ username, note_id: noteId, date: dateKey, photos: uploadedPhotos });
                onGetJournalByDay(username, dateKey);
            } catch (e) {
                console.error("Pending photo upload failed:", e);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notes, pendingPhotos]);

    // ---------- area/plant options ----------
    const currentYear = new Date().getFullYear();
    const areaOptions = useMemo(() =>
        safeArray(userareas)
            .filter((a) => a.id && a.name && a.year === currentYear)
            .map((a) => ({ id: a.id, name: a.name }))
            .sort((x, y) => x.name.localeCompare(y.name))
    , [userareas, currentYear]);

    const plantOptions = useMemo(() => {
        if (!newNoteArea) return [];
        const area = safeArray(userareas).find((a) => a.id === newNoteArea);
        return safeArray(area?.plants)
            .filter((p) => p.id)
            .map((p) => ({ id: p.id, name: p.species || p.name || "" }))
            .filter((p) => p.name);
    }, [userareas, newNoteArea]);

    const timelinePlantOptions = useMemo(() => {
        if (!timelineArea) return [];
        const area = safeArray(userareas).find((a) => a.id === timelineArea);
        return safeArray(area?.plants)
            .filter((p) => p.id)
            .map((p) => ({ id: p.id, name: p.species || p.name || "" }))
            .filter((p) => p.name);
    }, [userareas, timelineArea]);


    // ---------- handlers ----------
    const selectDay = (dateStr) => {
        setSelectedDateKey(dateStr);
        setJournalView("notes");
    };
    const handleDateClick = (info) => selectDay(info.dateStr);
    const handleEventClick = (info) => selectDay(toDateKey(info.event.start));

    const switchView = (view) => {
        setSearchTerm("");
        setJournalView(view);
        if (view === "timeline" && username) onGetJournalTimeline({ username });
    };

    // Switch to/from search view as the user types
    useEffect(() => {
        if (!searchTerm.trim()) {
            if (journalView === "search") setJournalView("notes");
        } else {
            setJournalView("search");
        }
    }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    const openAddNoteModal = () => {
        setNewNoteText("");
        setNewNoteArea("");
        setNewNotePlant("");
        setNewNoteFiles([]);
        setAddNoteModalOpen(true);
    };

    const closeAddNoteModal = () => setAddNoteModalOpen(false);

    const openPhotoModalForNote = (noteId) => {
        setActiveNoteId(noteId);
        setPhotoCaption("");
        setShowPhotoModal(true);
    };

    const closePhotoModal = () => {
        setShowPhotoModal(false);
        setActiveNoteId(null);
        setPhotoCaption("");
    };

    const handleCreateNote = async () => {
        if (!username || !selectedDateKey) return;
        const text = newNoteText.trim();
        if (!text && newNoteFiles.length === 0) return;

        setCreating(true);
        try {
            // Create the note first (no photos — we don't have a note_id yet)
            await onCreateNote({
                username,
                date: selectedDateKey,
                note_text: text || undefined,
                area_id: newNoteArea || undefined,
                plant_id: newNotePlant || undefined,
                photos: [],
            });

            // If files were selected, queue them — useEffect will attach once the note appears
            if (newNoteFiles.length > 0) {
                setPendingPhotos({ files: newNoteFiles, dateKey: selectedDateKey, noteText: text });
            }

            closeAddNoteModal();
            onGetJournalByDay(username, selectedDateKey);
            onGetJournalDays(username);
        } catch (e) {
            console.error("Create note failed:", e);
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteNote = async (note) => {
        const note_id = pickNoteId(note);
        if (!username || !note_id) return;
        try {
            await onDeleteNote(note_id);
            onGetJournalByDay(username, selectedDateKey);
            onGetJournalDays(username);
        } catch (e) {
            console.error("Delete note failed:", e);
        }
    };

    const handleUploadPhoto = async (e) => {
        e.preventDefault();
        if (!username || !selectedDateKey || !activeNoteId) return;
        const formData = new FormData(e.target);
        const file = formData.get("file");
        if (!file || !file.name) return;

        setUploading(true);
        try {
            const s3_key = await uploadPhotoToS3({ file, username, dateKey: selectedDateKey, noteId: activeNoteId });
            const url = await safeStorageGet(s3_key);
            if (url) setPhotoUrlByKey((prev) => ({ ...prev, [s3_key]: url }));

            const current = safeArray(notes).find((n) => pickNoteId(n) === activeNoteId);
            const nextPhotos = [...normalizePhotos(current), { s3_key, comment: photoCaption.trim() }];

            await onUpdateNote({ username, note_id: activeNoteId, date: selectedDateKey, photos: nextPhotos });
            closePhotoModal();
            onGetJournalByDay(username, selectedDateKey);
        } catch (err) {
            console.error("Upload photo failed:", err);
        } finally {
            setUploading(false);
        }
    };

    const handleDeletePhotoFromNote = async (note, s3_key) => {
        const note_id = pickNoteId(note);
        if (!username || !note_id || !s3_key) return;
        try {
            const nextPhotos = normalizePhotos(note).filter((p) => p.s3_key !== s3_key);
            await onUpdateNote({ username, note_id, date: selectedDateKey, photos: nextPhotos });
            setPhotoUrlByKey((prev) => { const c = { ...prev }; delete c[s3_key]; return c; });
            onGetJournalByDay(username, selectedDateKey);
        } catch (e) {
            console.error("Delete photo failed:", e);
        }
    };

    const noteFieldChange = async (target, note) => {
        const note_id = pickNoteId(note);
        if (!username || !note_id) return;
        try {
            const field = safeString(target?.props?.name);
            const value = target?.newValue;
            const payload = { username, note_id, date: selectedDateKey };
            if (field === "note_text") payload.note_text = safeString(value);
            await onUpdateNote(payload);
            onGetJournalByDay(username, selectedDateKey);
        } catch (e) {
            console.error("Note field update failed:", e);
        }
    };

    const handleNoteAreaChange = async (note, newAreaId) => {
        const note_id = pickNoteId(note);
        if (!username || !note_id) return;
        try {
            await onUpdateNote({ username, note_id, date: selectedDateKey, area_id: newAreaId || undefined, plant_id: undefined });
            onGetJournalByDay(username, selectedDateKey);
        } catch (e) { console.error("Note area update failed:", e); }
    };

    const handleNotePlantChange = async (note, newPlantId) => {
        const note_id = pickNoteId(note);
        if (!username || !note_id) return;
        try {
            await onUpdateNote({ username, note_id, date: selectedDateKey, area_id: note.area_id, plant_id: newPlantId || undefined });
            onGetJournalByDay(username, selectedDateKey);
        } catch (e) { console.error("Note plant update failed:", e); }
    };

    const photoCaptionChange = async (target, note, photoKey) => {
        const note_id = pickNoteId(note);
        if (!username || !note_id || !photoKey) return;
        try {
            const value = safeString(target?.newValue);
            const nextPhotos = normalizePhotos(note).map((p) =>
                p.s3_key === photoKey ? { ...p, comment: value } : p
            );
            await onUpdateNote({ username, note_id, date: selectedDateKey, photos: nextPhotos });
            onGetJournalByDay(username, selectedDateKey);
        } catch (e) {
            console.error("Caption update failed:", e);
        }
    };

    // ---------- render ----------
    const notesForDay = safeArray(notes);
    const searchResults = searchTerm.trim()
        ? safeArray(timelineNotes).filter((n) => {
            const term = searchTerm.trim().toLowerCase();
            if (safeString(n.note_text || n.text).toLowerCase().includes(term)) return true;
            return normalizePhotos(n).some((p) => safeString(p.comment).toLowerCase().includes(term));
        })
        : [];
    const carouselPhoto = allPhotosForDay[carouselIdx] || null;
    const carouselUrl = carouselPhoto ? photoUrlByKey[carouselPhoto.s3_key] : null;

    return (
        <div className="page-content">
            <style>{`
                .mini-calendar-wrapper .fc { font-size: 10px; }
                .mini-calendar-wrapper .fc-toolbar-title { display: none !important; }
                .mini-calendar-wrapper .fc-daygrid-day-number { padding: 0 !important; font-size: 10px !important; }
                .mini-dot { width: 12px; height: 12px; border-radius: 50%; margin: 2px auto 0; background: #6c757d; cursor: pointer; }
                .mini-calendar-wrapper .fc-event { background: transparent !important; border: none !important; box-shadow: none !important; }
                .section-title { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
                .photo-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
                .photo-thumb { width: 100%; height: 80px; object-fit: cover; border-radius: 8px; border: 1px solid rgba(0,0,0,0.08); cursor: pointer; transition: opacity 0.15s; }
                .photo-thumb:hover { opacity: 0.82; }
                .note-card { margin-bottom: 12px; }
                .btn-link-tight { padding: 0 6px !important; }
                .carousel-photo { width: 100%; height: 200px; object-fit: cover; border-radius: 10px; cursor: pointer; }
                .carousel-placeholder { height: 200px; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.04); border-radius: 10px; color: rgba(0,0,0,0.4); font-size: 13px; }
                .note-tag { display: inline-block; background: #EEF4EE; color: #4a6741; border-radius: 12px; padding: 2px 10px; font-size: 11px; margin-right: 4px; }
                .lightbox-img { width: 100%; max-height: 80vh; object-fit: contain; border-radius: 6px; }
            `}</style>

            <Container fluid>
                <Row className="mb-3" style={{ alignItems: "center" }}>
                    <Col>
                        <h4 style={{ marginBottom: 0 }}>Journal</h4>
                        <div style={{ color: "rgba(0,0,0,0.6)" }}>
                            {formatDateHuman(selectedDateKey)}
                            {error ? (
                                <span style={{ marginLeft: 10, color: "rgba(200,0,0,0.8)" }}>
                                    {safeString(error?.message || error)}
                                </span>
                            ) : null}
                        </div>
                    </Col>
                    <Col xs="auto">
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <Input
                                type="text"
                                bsSize="sm"
                                placeholder="Search notes…"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{ width: 180 }}
                            />
                            <div style={{ display: "flex", gap: 4 }}>
                                <Button size="sm"
                                    color={journalView === "notes" ? "success" : "light"}
                                    onClick={() => switchView("notes")}>
                                    Notes
                                </Button>
                                <Button size="sm"
                                    color={journalView === "timeline" ? "success" : "light"}
                                    onClick={() => switchView("timeline")}>
                                    Timeline
                                </Button>
                                {journalView === "search" && (
                                    <Button size="sm" color="success" disabled>
                                        Search
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row>
                    {/* Left: mini calendar + photo carousel */}
                    <Col md={4} lg={3}>
                        <Card className="mb-3">
                            <CardBody>
                                <div className="mini-calendar-wrapper">
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                                        <div style={{ display: "flex", gap: 6 }}>
                                            <Button size="sm" color="link" className="btn-link-tight"
                                                onClick={() => calendarRef.current?.getApi()?.prev?.()}>◀</Button>
                                            <Button size="sm" color="link" className="btn-link-tight"
                                                onClick={() => calendarRef.current?.getApi()?.today?.()}>•</Button>
                                            <Button size="sm" color="link" className="btn-link-tight"
                                                onClick={() => calendarRef.current?.getApi()?.next?.()}>▶</Button>
                                        </div>
                                        <div style={{ fontSize: 12, color: "rgba(0,0,0,0.65)" }}>{monthLabel}</div>
                                    </div>

                                    <FullCalendar
                                        ref={calendarRef}
                                        plugins={[dayGridPlugin, interactionPlugin]}
                                        initialView="dayGridMonth"
                                        headerToolbar={false}
                                        fixedWeekCount={false}
                                        showNonCurrentDates={false}
                                        height={240}
                                        dateClick={handleDateClick}
                                        eventClick={handleEventClick}
                                        events={miniCalendarEvents}
                                        eventBackgroundColor="transparent"
                                        eventBorderColor="transparent"
                                        eventContent={() => ({ html: '<div class="mini-dot"></div>' })}
                                        datesSet={(arg) => setMonthLabel(arg?.view?.title || "")}
                                    />
                                </div>
                            </CardBody>
                        </Card>

                        {/* Photo carousel */}
                        <Card className="mb-3">
                            <CardHeader style={{ backgroundColor: "#BEC8B6" }}>
                                <div className="section-title">
                                    <span>Photos</span>
                                    <Badge pill color="secondary">{allPhotosForDay.length}</Badge>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {allPhotosForDay.length === 0 ? (
                                    <div className="carousel-placeholder">No photos for this day</div>
                                ) : (
                                    <>
                                        {carouselUrl ? (
                                            <img
                                                src={carouselUrl}
                                                alt={carouselPhoto?.comment || "photo"}
                                                className="carousel-photo"
                                                onClick={() => setLightboxUrl(carouselUrl)}
                                            />
                                        ) : (
                                            <div className="carousel-placeholder">Loading…</div>
                                        )}

                                        {carouselPhoto?.comment ? (
                                            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.6)", marginTop: 6, fontStyle: "italic" }}>
                                                {carouselPhoto.comment}
                                            </div>
                                        ) : null}

                                        {carouselPhoto?.noteText ? (
                                            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginTop: 4 }}>
                                                {carouselPhoto.noteText}
                                            </div>
                                        ) : null}

                                        {allPhotosForDay.length > 1 && (
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 10 }}>
                                                <Button size="sm" color="secondary" outline
                                                    onClick={() => setCarouselIdx((i) => (i - 1 + allPhotosForDay.length) % allPhotosForDay.length)}>
                                                    ‹
                                                </Button>
                                                <span style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
                                                    {carouselIdx + 1} / {allPhotosForDay.length}
                                                </span>
                                                <Button size="sm" color="secondary" outline
                                                    onClick={() => setCarouselIdx((i) => (i + 1) % allPhotosForDay.length)}>
                                                    ›
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Right: tab bar + notes or timeline */}
                    <Col md={8} lg={9}>
                        <Card className="mb-3">
                            <CardHeader style={{ backgroundColor: "#BEC8B6" }}>
                                <div className="section-title">
                                    <span style={{ fontWeight: 500 }}>
                                        {journalView === "timeline" ? "Timeline" : journalView === "search" ? "Search Results" : "Notes"}
                                    </span>
                                    {journalView === "notes" && (
                                        <Button color="success" size="sm" onClick={openAddNoteModal} disabled={!username}>
                                            + Add Note
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardBody style={{ maxHeight: journalView === "notes" || journalView === "search" ? 600 : "none", overflowY: journalView === "notes" || journalView === "search" ? "auto" : "visible" }}>
                                {journalView === "search" ? (
                                    <>
                                        <div style={{ marginBottom: 12, padding: "6px 10px", background: "rgba(0,0,0,0.04)", borderRadius: 6, fontSize: 13, color: "rgba(0,0,0,0.65)" }}>
                                            {`${searchResults.length} result${searchResults.length !== 1 ? "s" : ""} for "${searchTerm}"`}
                                        </div>
                                        {searchResults.length === 0 ? (
                                            <div style={{ color: "rgba(0,0,0,0.5)" }}>No notes found.</div>
                                        ) : (
                                            searchResults.map((note) => {
                                                const noteId = pickNoteId(note);
                                                const photos = normalizePhotos(note);
                                                const noteAreaId = note?.area_id || "";
                                                const notePlantId = note?.plant_id || "";
                                                return (
                                                    <Card key={noteId || Math.random()} className="note-card" style={{ backgroundColor: "#f4f7f3" }}>
                                                        <CardHeader style={{ backgroundColor: "#f4f7f3", borderBottom: "none" }}>
                                                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                                                <span style={{ fontSize: 13, fontWeight: 600, color: "#4a6741" }}>{note.date}</span>
                                                                {noteAreaId && <span className="note-tag">📍 {lookupAreaName(userareas, noteAreaId)}</span>}
                                                                {notePlantId && <span className="note-tag">🌿 {lookupPlantName(userareas, noteAreaId, notePlantId)}</span>}
                                                            </div>
                                                        </CardHeader>
                                                        <CardBody style={{ backgroundColor: "#f4f7f3" }}>
                                                            {note.note_text && (
                                                                <div style={{ color: "rgba(0,0,0,0.8)", lineHeight: 1.5, marginBottom: photos.length ? 10 : 0 }}>
                                                                    {note.note_text}
                                                                </div>
                                                            )}
                                                            {photos.length > 0 && (
                                                                <div>
                                                                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginBottom: 6 }}>Photos ({photos.length})</div>
                                                                    <div className="photo-grid">
                                                                        {photos.map((p) => {
                                                                            const url = photoUrlByKey[p.s3_key];
                                                                            return (
                                                                                <div key={p.s3_key} style={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: 10, padding: 8 }}>
                                                                                    {url ? (
                                                                                        <img src={url} alt={p.comment || "photo"}
                                                                                            className="photo-thumb"
                                                                                            onClick={() => setLightboxUrl(url)}
                                                                                        />
                                                                                    ) : (
                                                                                        <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.03)", borderRadius: 8, fontSize: 12, color: "rgba(0,0,0,0.6)" }}>Loading…</div>
                                                                                    )}
                                                                                    {p.comment && <div style={{ marginTop: 4, fontSize: 12, color: "rgba(0,0,0,0.6)", fontStyle: "italic" }}>{p.comment}</div>}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </CardBody>
                                                    </Card>
                                                );
                                            })
                                        )}
                                    </>
                                ) : journalView === "timeline" ? (
                                    <TimelineView
                                        userareas={userareas}
                                        areaOptions={areaOptions}
                                        timelineArea={timelineArea}
                                        setTimelineArea={(v) => {
                                            setTimelineArea(v);
                                            setTimelinePlant("");
                                            if (v && username) onGetJournalTimeline({ username, area_id: v });
                                        }}
                                        timelinePlant={timelinePlant}
                                        setTimelinePlant={(v) => {
                                            setTimelinePlant(v);
                                            if (username) onGetJournalTimeline({ username, area_id: timelineArea, plant_id: v || undefined });
                                        }}
                                        timelinePlantOptions={timelinePlantOptions}
                                        timelineNotes={timelineNotes}
                                        timelineLoading={timelineLoading}
                                        photoUrlByKey={photoUrlByKey}
                                        onPhotoClick={setLightboxUrl}
                                    />
                                ) : notesForDay.length === 0 ? (
                                    <div style={{ color: "rgba(0,0,0,0.6)" }}>No notes for this day.</div>
                                ) : (
                                    notesForDay.map((note) => {
                                        const noteId = pickNoteId(note);
                                        const photos = normalizePhotos(note);
                                        const noteAreaId = note?.area_id || "";
                                        const notePlantId = note?.plant_id || "";
                                        const noteArea = safeArray(userareas).find((a) => a.id === noteAreaId);
                                        const notePlantOpts = safeArray(noteArea?.plants)
                                            .filter((p) => p.id)
                                            .map((p) => ({ id: p.id, name: p.species || p.name || "" }))
                                            .filter((p) => p.name);

                                        const isEditingThis = editingNoteId === noteId;
                                        return (
                                            <Card key={noteId || Math.random()} className="note-card" style={{ backgroundColor: "#f4f7f3" }}>
                                                <CardHeader style={{ backgroundColor: "#f4f7f3", borderBottom: "none" }}>
                                                    <div className="section-title">
                                                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                                            {isEditingThis ? (
                                                                <>
                                                                    <Input type="select" bsSize="sm"
                                                                        style={{ width: "auto", fontSize: 12 }}
                                                                        value={noteAreaId}
                                                                        onChange={(e) => handleNoteAreaChange(note, e.target.value)}>
                                                                        <option value="">📍 No area</option>
                                                                        {areaOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                                                                    </Input>
                                                                    <Input type="select" bsSize="sm"
                                                                        style={{ width: "auto", fontSize: 12 }}
                                                                        value={notePlantId}
                                                                        disabled={!noteAreaId || notePlantOpts.length === 0}
                                                                        onChange={(e) => handleNotePlantChange(note, e.target.value)}>
                                                                        <option value="">🌿 No plant</option>
                                                                        {notePlantOpts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                                    </Input>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {noteAreaId && <span className="note-tag">📍 {lookupAreaName(userareas, noteAreaId)}</span>}
                                                                    {notePlantId && <span className="note-tag">🌿 {lookupPlantName(userareas, noteAreaId, notePlantId)}</span>}
                                                                </>
                                                            )}
                                                            <Button color="link" size="sm" style={{ padding: "0 4px", fontSize: 12, color: "rgba(0,0,0,0.4)", textDecoration: "none" }}
                                                                onClick={() => setEditingNoteId(isEditingThis ? null : noteId)}>
                                                                {isEditingThis ? "✓ Done" : "✎ Edit"}
                                                            </Button>
                                                        </div>
                                                        <div style={{ display: "flex", gap: 8 }}>
                                                            <Button color="success" size="sm"
                                                                onClick={() => openPhotoModalForNote(noteId)}
                                                                disabled={!noteId}>
                                                                Add photo
                                                            </Button>
                                                            <Button color="danger" size="sm"
                                                                onClick={() => handleDeleteNote(note)}
                                                                disabled={!noteId}>
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </CardHeader>

                                                <CardBody style={{ backgroundColor: "#f4f7f3" }}>
                                                    <Editable
                                                        name="note_text"
                                                        value={safeString(note?.note_text || note?.text || "")}
                                                        display={(value) => (
                                                            <span style={{ color: "black" }}>
                                                                {safeString(value) || <em style={{ color: "rgba(0,0,0,0.4)" }}>Click to add description…</em>}
                                                            </span>
                                                        )}
                                                        dataType="text"
                                                        showButtons={false}
                                                        mode="inline"
                                                        handleSubmit={(target) => noteFieldChange(target, note)}
                                                    />

                                                    {photos.length > 0 && (
                                                        <div style={{ marginTop: 12 }}>
                                                            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginBottom: 6 }}>
                                                                Photos ({photos.length})
                                                            </div>
                                                            <div className="photo-grid">
                                                                {photos.map((p) => {
                                                                    const url = photoUrlByKey[p.s3_key];
                                                                    return (
                                                                        <div key={p.s3_key} style={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: 10, padding: 8 }}>
                                                                            {url ? (
                                                                                <img src={url} alt={p.comment || "photo"}
                                                                                    className="photo-thumb"
                                                                                    onClick={() => setLightboxUrl(url)}
                                                                                />
                                                                            ) : (
                                                                                <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.03)", borderRadius: 8, fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
                                                                                    Loading…
                                                                                </div>
                                                                            )}

                                                                            <div style={{ marginTop: 6, fontSize: 12 }}>
                                                                                <strong>Caption: </strong>
                                                                                <Editable
                                                                                    name="photo_comment"
                                                                                    value={p.comment}
                                                                                    display={(value) => (
                                                                                        <span style={{ color: "black" }}>{safeString(value) || "Click to add caption"}</span>
                                                                                    )}
                                                                                    dataType="text"
                                                                                    showButtons={false}
                                                                                    mode="inline"
                                                                                    handleSubmit={(target) => photoCaptionChange(target, note, p.s3_key)}
                                                                                />
                                                                            </div>

                                                                            <div style={{ marginTop: 8, textAlign: "right" }}>
                                                                                <Button size="sm" color="danger"
                                                                                    onClick={() => handleDeletePhotoFromNote(note, p.s3_key)}>
                                                                                    Remove
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        );
                                    })
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Add Note modal */}
            <Modal isOpen={addNoteModalOpen} toggle={closeAddNoteModal}>
                <ModalHeader toggle={closeAddNoteModal}>Add Note — {formatDateHuman(selectedDateKey)}</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>What happened?</Label>
                        <Input
                            type="textarea"
                            rows={4}
                            value={newNoteText}
                            onChange={(e) => setNewNoteText(e.target.value)}
                            placeholder="Describe what you observed, did, or noticed today…"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Area <span style={{ color: "rgba(0,0,0,0.45)", fontWeight: 400 }}>(optional)</span></Label>
                        <Input type="select" value={newNoteArea}
                            onChange={(e) => { setNewNoteArea(e.target.value); setNewNotePlant(""); }}>
                            <option value="">— All areas —</option>
                            {areaOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </Input>
                    </FormGroup>

                    <FormGroup>
                        <Label>Plant <span style={{ color: "rgba(0,0,0,0.45)", fontWeight: 400 }}>(optional)</span></Label>
                        <Input type="select" value={newNotePlant}
                            onChange={(e) => setNewNotePlant(e.target.value)}
                            disabled={!newNoteArea || plantOptions.length === 0}>
                            <option value="">— All plants —</option>
                            {plantOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </Input>
                    </FormGroup>

                    <FormGroup>
                        <Label>Photos <span style={{ color: "rgba(0,0,0,0.45)", fontWeight: 400 }}>(optional)</span></Label>
                        <Input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => setNewNoteFiles(Array.from(e.target.files || []))}
                        />
                        {newNoteFiles.length > 0 && (
                            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.6)", marginTop: 4 }}>
                                {newNoteFiles.length} file{newNoteFiles.length > 1 ? "s" : ""} selected
                            </div>
                        )}
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeAddNoteModal} disabled={creating}>Cancel</Button>
                    <Button color="success" onClick={handleCreateNote}
                        disabled={creating || (!newNoteText.trim() && newNoteFiles.length === 0)}>
                        {creating ? "Saving…" : "Save"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Add Photo modal (for existing notes) */}
            <Modal isOpen={showPhotoModal} toggle={closePhotoModal}>
                <ModalHeader toggle={closePhotoModal}>Add Photo</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleUploadPhoto}>
                        <FormGroup>
                            <Label>Caption (optional)</Label>
                            <Input value={photoCaption} onChange={(e) => setPhotoCaption(e.target.value)} />
                        </FormGroup>
                        <FormGroup>
                            <Label>File</Label>
                            <Input name="file" type="file" accept="image/*" />
                        </FormGroup>
                        <Button color="success" type="submit" disabled={uploading}>
                            {uploading ? "Uploading…" : "Upload"}
                        </Button>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closePhotoModal} disabled={uploading}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* Lightbox */}
            <Modal isOpen={!!lightboxUrl} toggle={() => setLightboxUrl(null)} size="xl" centered>
                <ModalBody style={{ padding: 8, background: "#111" }}>
                    {lightboxUrl && (
                        <img src={lightboxUrl} alt="full size" className="lightbox-img" />
                    )}
                </ModalBody>
                <ModalFooter style={{ background: "#111", borderTop: "none", padding: "6px 8px" }}>
                    <Button color="secondary" onClick={() => setLightboxUrl(null)}>Close</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

JournalLog.propTypes = {
    daysWithNotes: PropTypes.array,
    notes: PropTypes.array.isRequired,
    timelineNotes: PropTypes.array,
    timelineLoading: PropTypes.bool,
    error: PropTypes.any,
    userareas: PropTypes.array,

    onGetJournalDays: PropTypes.func.isRequired,
    onGetJournalByDay: PropTypes.func.isRequired,
    onCreateNote: PropTypes.func.isRequired,
    onUpdateNote: PropTypes.func.isRequired,
    onDeleteNote: PropTypes.func.isRequired,
    onGetAreas: PropTypes.func.isRequired,
    onGetJournalTimeline: PropTypes.func.isRequired,
};

const mapStateToProps = ({ journal, goals }) => ({
    daysWithNotes: Array.isArray(journal.daysWithNotes) ? journal.daysWithNotes : [],
    notes: Array.isArray(journal.notes) ? journal.notes : [],
    timelineNotes: Array.isArray(journal.timelineNotes) ? journal.timelineNotes : [],
    timelineLoading: journal.timelineLoading || false,
    error: journal.error,
    userareas: Array.isArray(goals?.areas) ? goals.areas : [],
});

const mapDispatchToProps = (dispatch) => ({
    onGetJournalDays: (username) => dispatch(getJournalDays({ username })),
    onGetJournalByDay: (username, date) => dispatch(getJournalByDay({ username, date })),
    onCreateNote: (payload) => dispatch(createJournalEntry(payload)),
    onUpdateNote: (payload) => dispatch(updateJournalEntry(payload)),
    onDeleteNote: (noteId) => dispatch(deleteJournalEntry(noteId)),
    onGetAreas: () => dispatch(getAreas()),
    onGetJournalTimeline: (payload) => dispatch(getJournalTimeline(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(JournalLog));
